import {
  ReceiptModel,
  usePrintReceipt,
  useUpdateReceiptStatus,
} from 'api-hooks/receipt';
import { AuthorizationRules, NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import Button from 'components/elements/buttons/button';
import Popover, { PopoverMenuType } from 'components/popover';
import FileSaver from 'file-saver';
import useApprovalAction from 'hooks/use-approval-action';
import { useAuthorization } from 'hooks/use-authorization';
import useDialog from 'hooks/use-dialog';
import useDismiss from 'hooks/use-dismiss';
import useNavigation from 'hooks/use-navigation';
import useResolveApproval from 'hooks/use-resolve-approval';
import useRevertDraftAction from 'hooks/use-revert-draft-action';
import useVoidAction, { isDataActive } from 'hooks/use-void-action';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

interface Props {
  receipt: ReceiptModel;
}

export default function ReceiptActionComponent(props: Props) {
  const { t } = useTranslation();

  const { can } = useAuthorization();
  const { navigate } = useNavigation();
  const { receipt } = props;
  const onDismiss = useDismiss('receipts', receipt.id);
  const dialog = useDialog();
  const isActive = isDataActive(receipt.status);
  const { mutateAsync: printMutate } = usePrintReceipt();
  const printReceiptPermission = can(AuthorizationRules.ReceiptsExport);
  const onPrintReceipt = React.useCallback(async () => {
    try {
      const result = await printMutate({
        id: receipt!.id!,
      });
      FileSaver.saveAs(result as Blob, 'receipts');
    } catch (e) {
      e?.message && notification.error({ message: e.message });
    }
  }, [printMutate, receipt]);
  const printReceiptAction: PopoverMenuType = {
    label: t('common:export', { extra: t('common:receipt') }),
    onClick: onPrintReceipt,
    isAllowed: printReceiptPermission && isActive,
    key: `export-receipt-${receipt.id}`,
  };
  const addToArPermission =
    can(AuthorizationRules.AccountReceivablesCreate) && isActive;
  const addToAr = () => {
    navigate(NavigationRoutes.AccountReceivablesCreate, {
      receipt,
    });
  };
  const addToArAction: PopoverMenuType = {
    key: `add-to-ar-${receipt.id}`,
    label: t('receipt:create_receivable'),
    onClick: addToAr,
    isAllowed: addToArPermission,
  };

  const { voidAction } = useVoidAction('receipts', receipt);

  const { mutateAsync: mutateUpdateStatus, isLoading: isLoadingUpdateStatus } =
    useUpdateReceiptStatus();
  const updateStatusPermission =
    can(AuthorizationRules.ReceiptsUpdateReceiptStatus) && isActive;
  const onUpdateStatus = React.useCallback(() => {
    dialog.showConfirmation({
      title: t('common:confirmation'),
      message: t('common:confirmation_text'),
      onPositiveAction: async (onClose) => {
        try {
          onClose();
          await mutateUpdateStatus({
            id: receipt!.id!,
            body: { action: 'complete' },
          });
          onDismiss(receipt?.id);
        } catch (error) {
          notification.error({ message: error.message });
        }
      },
      onNegativeAction: (onClose) => onClose(),
    });
  }, [dialog, mutateUpdateStatus, onDismiss, receipt, t]);

  const updateStatusComponent = updateStatusPermission && (
    <Button isLoading={isLoadingUpdateStatus} onClick={onUpdateStatus}>
      {t('status:completed')}
    </Button>
  );

  const actions = [printReceiptAction, addToArAction, voidAction].filter(
    (action) => action.isAllowed,
  );

  const { resolverButton } = useResolveApproval('receipts', receipt);
  const { requestApprovalButton } = useApprovalAction('receipts', receipt);
  const { revertDraftButton } = useRevertDraftAction('receipts', receipt);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {revertDraftButton}
      {resolverButton}
      {requestApprovalButton}
      <Popover actions={actions} />
      {updateStatusComponent}
    </div>
  );
}
